// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type AssetsInfoResp } from '@shared/data-access/interfaces/api/ai/assets';
import { type Stock } from '@shared/data-access/interfaces/api/ai/screener';
import { type TradeDetails } from '@shared/data-access/interfaces/redux/trades';
import { getAssetsInfo } from '@shared/data-access/transformers/assets/get-assets-info';
import { arrayToMap } from '@advisor-app/features/utils/array-to-map';
import type { ThunkDispatch } from '@reduxjs/toolkit';

export type TradeColumnName = NonNullable<TradeDetails['columnName']>;
export type ToAssetDetails = NonNullable<TradeDetails['to_asset_details']>;

export const getAssetDfiUrl = (info?: AssetsInfoResp): string | null | undefined =>
  info && 'dfi_url' in info ? info.dfi_url : undefined;

export const buildTradeColumnName = (
  info: AssetsInfoResp | undefined,
  fallback: Partial<TradeColumnName> & { ticker?: string },
): TradeColumnName => ({
  asset_type: info?.asset_type ?? fallback.asset_type,
  name: info?.name ?? fallback.name ?? fallback.ticker ?? '',
  url_logo: fallback.url_logo ?? (info as Stock | undefined)?.url_logo,
  url_morningstar:
    fallback.url_morningstar ?? (info as Stock | undefined)?.url_morningstar,
  dfi_url: getAssetDfiUrl(info) ?? fallback.dfi_url ?? null,
});

export const buildToAssetDetails = (
  info: AssetsInfoResp | undefined,
  fallbackTicker?: string,
  fallback?: Partial<ToAssetDetails>,
): ToAssetDetails => ({
  name: info?.name ?? fallback?.name ?? fallbackTicker ?? '',
  asset_type: info?.asset_type ?? fallback?.asset_type,
  url_logo: fallback?.url_logo ?? (info as Stock | undefined)?.url_logo,
  url_morningstar:
    fallback?.url_morningstar ?? (info as Stock | undefined)?.url_morningstar,
  dfi_url: getAssetDfiUrl(info) ?? fallback?.dfi_url ?? null,
});

export const fetchAssetInfoMap = async (
  tickers: string[],
  dispatch: ThunkDispatch<any, any, any>,
) => {
  const uniqueTickers = Array.from(new Set(tickers.filter(Boolean)));

  if (!uniqueTickers.length) {
    return new Map<string, AssetsInfoResp>();
  }

  const assetInfo = await getAssetsInfo(uniqueTickers, dispatch);

  return arrayToMap(assetInfo, 'ticker');
};
